import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { selectSection, openRatingRequest } from 'app/slices/ahuBoardSlice';
import { ahuBoardBus } from '../bus/ahuBoardBus';

/**
 * Phase 6e1 — Fan static-pressure popup (§12.2, fan_sp_popup.js +
 * fan_sp_popup.tmpl, markup faithful): accumulates `rerate:fan:data` notices
 * (sorted by air_stream_id, deduped by id, 1 s debounce), exact copy strings,
 * RERATE chain (select fan section → components tab → rating open — also on
 * `check:fans:need_to_rate`), ACCEPT clears.
 */

interface FanNotice {
  id: number;
  air_stream: string;
  air_stream_id: number;
  tag?: string | null;
  section: { id: number };
  model: { isp?: unknown; esp?: unknown; esp_desired?: unknown };
  prev_model: { isp?: unknown };
}

const FanSpPopup = () => {
  const dispatch = useAppDispatch();
  const [fans, setFans] = useState<FanNotice[]>([]);
  const [visible, setVisible] = useState(false);
  const queue = useRef<FanNotice[]>([]);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const offs = [
      ahuBoardBus.on('rerate:fan:data', (payload) => {
        const notice = payload as unknown as FanNotice;
        queue.current = queue.current.filter((f) => f.id !== notice.id);
        queue.current.push(notice);
        queue.current.sort((a, b) => (a.air_stream_id ?? 0) - (b.air_stream_id ?? 0));
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          setFans([...queue.current]);
          setVisible(queue.current.length > 0);
        }, 1000);
      }),
      ahuBoardBus.on('check:fans:need_to_rate', () => {
        rerate();
      }),
    ];
    return () => {
      offs.forEach((off) => off());
      if (timer.current) window.clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rerate = () => {
    const fan = queue.current.shift();
    setFans([...queue.current]);
    if (!fan) {
      setVisible(false);
      return;
    }
    ahuBoardBus.emit('force:select:section', fan.section.id, false, true);
    dispatch(selectSection({ sectionId: fan.section.id, tab: 'components' }));
    ahuBoardBus.emit('select:section', { section: { id: fan.section.id }, tab: 'components' });
    window.setTimeout(() => dispatch(openRatingRequest(fan.section.id)), 500);
    setVisible(queue.current.length > 0);
  };

  const accept = () => {
    queue.current = [];
    setFans([]);
    setVisible(false);
  };

  if (!visible || !fans.length) return null;

  const fmt = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : String(v ?? '');
  };

  return (
    <div className="popup-wrapper width-small active">
      <div className="popup-content">
        <h2 className="popup-heading">Heads Up!</h2>
        <div>
          <div className="clearfix popup-content-part">
            {fans.map((fan) => (
              <div className="each-fan" key={fan.id}>
                <p style={{ minHeight: 'unset' }}>
                  Your {fan.air_stream} Air Stream static pressures have changed.
                </p>
                <span className="fan-isp-info">
                  <span className="first-item">ISP Previous / New</span>
                  <span>
                    {String(fan.prev_model?.isp ?? '')} / {String(fan.model?.isp ?? '')} in WC
                  </span>
                </span>
                <span className="fan-isp-info">
                  <span className="first-item"> ESP Desired / Actual</span>
                  <span>
                    {fan.model?.esp_desired != null && fan.model.esp_desired !== ''
                      ? fmt(fan.model.esp_desired)
                      : ' _ _ _ '}{' '}
                    / {String(fan.model?.esp ?? '')} in WC
                  </span>
                </span>
              </div>
            ))}
            <p>
              Do you want to accept the new static pressures or rerate your{' '}
              {fans.map((fan, idx) => (
                <span key={fan.id}>
                  {fan.air_stream} Fan{fan.tag ? ` (${fan.tag})` : ''}
                  {fans.length > 1 && idx !== fans.length - 1
                    ? idx === fans.length - 2
                      ? ' and '
                      : ', '
                    : ''}
                </span>
              ))}
              ?
            </p>
          </div>
          <div className="popup-control-buttons">
            <button className="default-btn not-highlighted-btn" name="rerate" onClick={rerate}>
              RERATE
            </button>
            <button className="accept highlighted-btn" name="cancel" autoFocus onClick={accept}>
              ACCEPT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FanSpPopup;
